import type {
  AdminReviewDoc, DirectSendMailboxes, EmailDraftMeta, ReviewListFilter,
} from "../api/admin";

/** "Open" is the inbox: pending drafts plus the ones whose author is working
 * in requested changes – a draft never leaves the list while it is in a round. */
export const DRAFT_FILTERS: { value: ReviewListFilter; label: string }[] = [
  { value: "open", label: "Open" },
  { value: "changes_requested", label: "Changes requested" },
  { value: "approved", label: "Approved" },
  { value: "done", label: "Done" },
  { value: "rejected", label: "Rejected" },
  { value: "withdrawn", label: "Withdrawn" },
  { value: "all", label: "All" },
];

export const PRIORITY_LABELS: Record<number, string> = {
  1: "P1 Urgent", 2: "P2 Important", 3: "P3 Routine",
};

export function emailDraftFilters(params: URLSearchParams) {
  // `pending` is the inbox's former name for "open"; old links keep working.
  const raw = params.get("status") === "pending" ? "open" : params.get("status");
  const status = DRAFT_FILTERS.find((f) => f.value === raw)?.value ?? "open";
  const priority = params.get("priority") ?? "all";
  return {
    status,
    topic: params.get("topic") || "all",
    /** `all`, `none` (drafts of no campaign) or a campaign slug. */
    campaign: params.get("campaign") || "all",
    priority: ["1", "2", "3", "unprioritized"].includes(priority) ? priority : "all",
  };
}

export function matchesDraftFilters(doc: AdminReviewDoc, params: URLSearchParams) {
  const { topic, campaign, priority } = emailDraftFilters(params);
  return (topic === "all" || (doc.topic?.id ?? "uncategorized") === topic)
    && (campaign === "all" || (draftCampaign(doc)?.slug ?? "none") === campaign)
    && (priority === "all" || (doc.priority == null ? "unprioritized" : String(doc.priority)) === priority);
}

/** The campaign a draft belongs to – through its bundle –, or null. */
export interface DraftCampaign {
  slug: string;
  name: string;
}

export function draftCampaign(doc: AdminReviewDoc): DraftCampaign | null {
  const slug = doc.bundle?.campaign_slug;
  return slug ? { slug, name: doc.bundle?.campaign_name || slug } : null;
}

/** Epoch ms of a server timestamp; 0 when absent. Server timestamps are UTC
 * but older serializers omit the Z suffix. */
export function serverTime(raw: string | null | undefined) {
  return raw ? Date.parse(/[Zz]|[+-]\d\d:\d\d$/.test(raw) ? raw : `${raw}Z`) : 0;
}

export function draftCreatedAt(doc: AdminReviewDoc) {
  return serverTime(doc.created_at ?? doc.submitted_at);
}

/** A round in progress: waiting on the owner or on the author. */
export function isOpenDraft(doc: Pick<AdminReviewDoc, "status">) {
  return doc.status === "pending" || doc.status === "changes_requested";
}

/** How a finished email left: "sent" when the server sent it (Approve &
 * send), "drafted" when the seat created the mailbox draft – both end `done`.
 * Null for a document and for an email still in review. */
export function emailOutcome(doc: Pick<AdminReviewDoc, "kind" | "status" | "sent_directly">): "sent" | "drafted" | null {
  if (doc.kind !== "email") return null;
  if (doc.sent_directly) return "sent";
  return doc.status === "done" ? "drafted" : null;
}

export function draftAgeSeconds(doc: AdminReviewDoc) {
  return Math.max(0, (Date.now() - (draftCreatedAt(doc) || Date.now())) / 1000);
}

export interface EmailDraftGroup {
  id: string;
  name: string;
  /** Set for a campaign's group; a topic group holds drafts of no campaign. */
  campaign: DraftCampaign | null;
  drafts: AdminReviewDoc[];
}

/** A campaign's drafts group by campaign, every other draft by its topic. */
export function groupEmailDrafts(rows: AdminReviewDoc[]): EmailDraftGroup[] {
  const groups = new Map<string, EmailDraftGroup>();
  for (const doc of rows) {
    const campaign = draftCampaign(doc);
    const id = campaign ? `campaign:${campaign.slug}` : doc.topic?.id ?? "uncategorized";
    const group = groups.get(id)
      ?? { id, name: campaign?.name ?? doc.topic?.name ?? "Uncategorized", campaign, drafts: [] };
    group.drafts.push(doc);
    groups.set(id, group);
  }
  // A group keeps its place while the author revises one of its drafts.
  const rank = (docs: AdminReviewDoc[]) => {
    const open = docs.filter(isOpenDraft);
    return [
      Math.min(999, ...open.map((d) => d.priority ?? 999)),
      Math.min(Infinity, ...open.map(draftCreatedAt)),
    ];
  };
  return [...groups.values()].sort((a, b) => {
    const ar = rank(a.drafts), br = rank(b.drafts);
    return ar[0] - br[0] || ar[1] - br[1] || a.name.localeCompare(b.name);
  });
}

/** Opening a draft establishes the context of the group it shows in, also
 * for direct links: its campaign, or its topic among drafts of no campaign. */
export function draftReviewSearch(doc: AdminReviewDoc, search: string) {
  const params = new URLSearchParams(search);
  const campaign = draftCampaign(doc);
  if (!params.has("campaign") || params.get("campaign") === "all") {
    params.set("campaign", campaign?.slug ?? "none");
  }
  if (!campaign && (!params.has("topic") || params.get("topic") === "all")) {
    params.set("topic", doc.topic?.id ?? "uncategorized");
  }
  return `?${params.toString()}`;
}

/** One file of an email draft: an attachment that is sent, or a local file
 * for the review only. `localPath` opens on the owner's Mac via Jarvis Helper. */
export interface EmailDraftFile {
  name: string;
  localPath: string | null;
  reviewOnly: boolean;
  needsSignature: boolean;
}

export function emailDraftFiles(meta: EmailDraftMeta | null | undefined): EmailDraftFile[] {
  const local = meta?.attachment_local_paths ?? [];
  const names = meta?.attachment_names ?? [];
  const toSign = new Set(meta?.signature_required ?? []);
  // `signature_required` holds server names; the row shows the name the recipient sees.
  const sent = (meta?.attachments ?? []).map((name, i) => ({
    name: names[i] ?? name, localPath: local[i] ?? null, reviewOnly: false, needsSignature: toSign.has(name),
  }));
  const review = (meta?.review_files ?? []).map((path) => ({
    name: path.split("/").pop() || path, localPath: path, reviewOnly: true, needsSignature: false,
  }));
  return [...sent, ...review];
}

/**
 * The mailbox "Approve & send" sends this draft from, or null when the server
 * cannot send it – the client mirror of `direct_send_eligible` plus the
 * mailbox check. Attachments go out with it (up to 3 MB each, server-checked). An envelope without a mailbox goes out from the owner's
 * default mailbox, the one the seat would draft in.
 */
export function directSendMailbox(
  meta: EmailDraftMeta | null | undefined,
  send: DirectSendMailboxes | undefined,
): string | null {
  if (!send || !meta?.to?.length) return null;
  const address = (meta.mailbox_address || send.default || "").toLowerCase();
  return address && send.mailboxes.includes(address) ? address : null;
}

/** Why the draft cannot be approved yet, or null – the client mirror of the
 * server's `approval_block`, which refuses the finish anyway. */
export function signatureBlock(meta: EmailDraftMeta | null | undefined): string | null {
  const server = meta?.attachments ?? [];
  const names = meta?.attachment_names ?? [];
  const unsigned = (meta?.signature_required ?? [])
    .filter(Boolean)
    .map((name) => names[server.indexOf(name)] ?? name);
  return unsigned.length
    ? `Sign ${unsigned.join(", ")} first – the employee resubmits the signed file, then it can be approved`
    : null;
}

/** "German" / "English" – the language of a translation the seat attached. */
export function translationLabel(language?: string | null): string {
  return language === "en" ? "English" : "German";
}
